"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EBayEGDClient = void 0;
const base_client_1 = require("@deliverr/base-client");
const EBayEGDError_1 = require("./EBayEGDError");
const client_auth_1 = require("@deliverr/client-auth");
const core_auth_1 = require("@deliverr/core-auth");
class EBayEGDClient extends base_client_1.ApiBasicAuthClient {
    constructor(config = {}) {
        const { authEngine } = config;
        const clientConfig = Object.assign(Object.assign({}, config), { baseURL: `${process.env.EBAY_EGD_SERVICE_URL}/v1`, authEngine });
        if (authEngine === core_auth_1.AuthEngine.COGNITO_INTERNAL) {
            clientConfig.tokenRefreshFn = new client_auth_1.OAuthClientTokenBuilder(clientConfig).getTokenFn();
        }
        else if (authEngine === core_auth_1.AuthEngine.BASIC) {
            clientConfig.basicAuthUser = config.basicAuthUser || process.env.EBAY_EGD_BASIC_AUTH_USER;
            clientConfig.basicAuthPassword = config.basicAuthPassword || process.env.EBAY_EGD_BASIC_AUTH_PASSWORD;
        }
        super(clientConfig);
    }
    async initOAuth() {
        try {
            return await this.get(`/ebay/oauth/init`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async completeOAuth(sellerId, authCode) {
        try {
            await this.post(`/ebay/oauth/token`, { sellerId, authCode });
        }
        catch (err) {
            switch (err.response && err.response.status) {
                case 400:
                    throw (0, base_client_1.mapError)(err, [EBayEGDError_1.EBayEGDError.INVALID_CODE]);
                default:
                    throw (0, base_client_1.mapError)(err);
            }
        }
    }
    async getEGDStatus(sellerId) {
        try {
            return await this.get(`/ebay/${sellerId}/status`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getEGDConnectionStatus(sellerId) {
        try {
            return await this.get(`/seller/${sellerId}/connectionstatus`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async deactivateEGD(sellerId) {
        try {
            await this.delete(`/ebay/${sellerId}`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async validateRateTables(sellerId) {
        try {
            await this.post(`/rate-tables/${sellerId}/validate`, null);
            return true;
        }
        catch (err) {
            switch (err.response && err.response.status) {
                case 400:
                    return false;
                default:
                    throw (0, base_client_1.mapError)(err);
            }
        }
    }
    async updateAvailabilityForDsku(dsku) {
        try {
            await this.post(`/availability/refresh/${dsku}`, null);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async updateAvailabilityForAllListings() {
        try {
            await this.post(`/availability/refresh`, null);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async enableFastTag(sellerId, dsku) {
        try {
            await this.put(`/seller/${sellerId}/dsku/${dsku}`, { isActive: true });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [EBayEGDError_1.EBayEGDError.NOT_FOUND]);
        }
    }
    async disableFastTag(sellerId, dsku) {
        try {
            await this.put(`/seller/${sellerId}/dsku/${dsku}`, { isActive: false });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [EBayEGDError_1.EBayEGDError.NOT_FOUND]);
        }
    }
    async getLinkForDsku(dsku) {
        try {
            return await this.get(`/product/dsku/${dsku}/listings`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async updateLinkForDsku(sellerId, dsku, url) {
        try {
            return await this.put(`/product/seller/${sellerId}/dsku/${dsku}/listings`, {
                url,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [EBayEGDError_1.EBayEGDError.INVALID_URL]);
        }
    }
    async getDskuStateCoverage(dsku) {
        try {
            return await this.get(`/availability/dsku/${dsku}/coverage`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getStateCoverageByServiceLevel(dsku) {
        const coverage = await this.getDskuStateCoverage(dsku);
        return { 3: coverage };
    }
    async isFastTagEnabled(dsku) {
        try {
            return await this.get(`/product/dsku/${dsku}/active`);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getAllActiveSellers(limit, offset) {
        try {
            return await this.get("/oauth/sellers", {
                limit,
                offset,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getSellers(limit, offset, filters) {
        try {
            return await this.get("/sellers", Object.assign({ limit,
                offset }, filters));
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async refreshSeller(sellerId) {
        try {
            return await this.post(`/availability/${sellerId}/refresh`, {});
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async updateSeller(sellerId, update) {
        try {
            return await this.put(`/seller/${sellerId}`, update);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [EBayEGDError_1.EBayEGDError.NOT_FOUND]);
        }
    }
    async getSellersToRefresh(excludeRefreshedSince, limit, offset) {
        try {
            return await this.get("/sellers/refresh", {
                excludeRefreshedSince: excludeRefreshedSince.toISOString(),
                limit,
                offset,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getSellersWithoutActiveToken(excludeNotifiedSince, limit, offset) {
        try {
            return await this.get("/sellers/without-active-token", {
                excludeNotifiedSince: excludeNotifiedSince.toISOString(),
                limit,
                offset,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getOrderDeliveryDetails(sellerId, dskus, orderCreationTime, destinationZip, proposedRequiredDeliveryInDays) {
        try {
            return await this.get(`/order/${sellerId}/delivery`, {
                dskus,
                orderCreationTime,
                destinationZip,
                proposedRequiredDeliveryInDays,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
}
exports.EBayEGDClient = EBayEGDClient;
exports.default = EBayEGDClient;
//# sourceMappingURL=EBayEGDClient.js.map